import type { SearchHit, SearchResult } from "@klyra/shared";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
	CalendarDays,
	IdCard,
	type LucideIcon,
	Phone,
	Receipt,
	Search,
	SearchX,
	User,
} from "lucide-react";
/**
 * Global search — command-palette modal across patients, appointments,
 * invoices. Triggered by:
 *   - Click on header search button
 *   - Cmd/Ctrl+K from anywhere in the staff app
 *
 * Search runs after a 200ms debounce. Up/Down arrows navigate, Enter
 * opens the focused row. Esc closes. On open the input is focused and the
 * previously-focused element is remembered, then restored on close so a
 * keyboard user returns to where they were.
 *
 * Results are grouped by type with icons. Each row deep-links to the
 * authoritative page for that record.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { search } from "../lib/api.ts";
import { formatNumber } from "../lib/locale.ts";
import { DateText } from "./DateText.tsx";

/**
 * Localized appointment/invoice status label. Keys live in
 * common.json → search.status.* (ar + en) so the palette follows the UI
 * language instead of always showing Arabic.
 */
function statusLabel(
	t: (key: string, opts?: { defaultValue?: string }) => string,
	status: string,
): string {
	return t(`search.status.${status}`, { defaultValue: status });
}

interface Props {
	open: boolean;
	onClose: () => void;
}

export function GlobalSearch({ open, onClose }: Props) {
	const { t } = useTranslation("common");
	const navigate = useNavigate();

	const [rawQ, setRawQ] = useState("");
	const [debounced, setDebounced] = useState("");
	const [focused, setFocused] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);

	// Reset state when re-opening (so a stale query doesn't flash), focus the
	// input, and remember the previously-focused element so we can restore focus
	// to it on close (keyboard users return to the trigger, not <body>).
	useEffect(() => {
		if (open) {
			setRawQ("");
			setDebounced("");
			setFocused(0);
			const prevFocused = document.activeElement as HTMLElement | null;
			// Defer focus to next tick so the input is mounted
			const id = setTimeout(() => inputRef.current?.focus(), 0);
			return () => {
				clearTimeout(id);
				prevFocused?.focus?.();
			};
		}
	}, [open]);

	// Debounce typing so we don't flood the API on every keystroke
	useEffect(() => {
		const id = setTimeout(() => setDebounced(rawQ.trim()), 200);
		return () => clearTimeout(id);
	}, [rawQ]);

	const query = useQuery({
		queryKey: ["global-search", debounced],
		queryFn: () => search.global(debounced, 5),
		enabled: open && debounced.length > 0,
		staleTime: 30_000,
	});

	const flatHits = useMemo<SearchHit[]>(
		() => flatten(query.data),
		[query.data],
	);

	useEffect(() => {
		if (focused >= flatHits.length) setFocused(0);
	}, [flatHits.length, focused]);

	function go(hit: SearchHit) {
		onClose();
		if (hit.type === "patient") {
			navigate({ to: "/patients/$patientId", params: { patientId: hit.id } });
		} else if (hit.type === "appointment") {
			navigate({
				to: "/appointments/$appointmentId",
				params: { appointmentId: hit.id },
			});
		} else if (hit.type === "invoice") {
			navigate({
				to: "/invoices/$invoiceId",
				params: { invoiceId: hit.id },
			});
		}
	}

	function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setFocused((i) => Math.min(flatHits.length - 1, i + 1));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setFocused((i) => Math.max(0, i - 1));
		} else if (e.key === "Enter" && flatHits[focused]) {
			e.preventDefault();
			go(flatHits[focused]!);
		} else if (e.key === "Escape") {
			e.preventDefault();
			onClose();
		}
	}

	if (!open) return null;

	return (
		<div
			// biome-ignore lint/a11y/useSemanticElements: custom command-palette modal with backdrop + manual focus/ESC handling; a native <dialog> (showModal) would change focus/backdrop behavior
			role="dialog"
			aria-modal="true"
			aria-label={t("search.title", { defaultValue: "بحث" })}
			className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8"
			style={{ paddingTop: "max(2rem, env(safe-area-inset-top))" }}
		>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop; close is reachable via the explicit Esc handler on the focused search input */}
			<div
				className="absolute inset-0 bg-black/40 backdrop-blur-sm"
				onClick={onClose}
				aria-hidden
			/>
			<div className="relative w-full max-w-2xl rounded-2xl bg-card shadow-2xl border border-border overflow-hidden">
				<div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
					<Search
						className="size-5 shrink-0 text-muted-foreground"
						strokeWidth={2}
						aria-hidden
					/>
					<input
						ref={inputRef}
						type="text"
						value={rawQ}
						onChange={(e) => setRawQ(e.target.value)}
						onKeyDown={onKeyDown}
						placeholder={t("search.placeholder", {
							defaultValue: "ابحث عن مريض أو موعد أو فاتورة…",
						})}
						className="flex-1 bg-transparent text-base sm:text-sm outline-none placeholder:text-muted-foreground"
						autoComplete="off"
						spellCheck={false}
					/>
					<kbd className="hidden sm:inline text-xs text-muted-foreground border border-border rounded px-1.5 py-0.5">
						Esc
					</kbd>
				</div>

				<div className="max-h-[60vh] overflow-y-auto">
					{debounced.length === 0 ? (
						<EmptyHint />
					) : query.isLoading ? (
						<p className="py-8 text-center text-sm text-muted-foreground">
							{t("search.loading", { defaultValue: "جاري البحث…" })}
						</p>
					) : flatHits.length === 0 ? (
						<div className="py-10 text-center">
							<SearchX
								className="size-8 mx-auto mb-1 text-muted-foreground"
								strokeWidth={1.5}
								aria-hidden
							/>
							<p className="text-sm text-muted-foreground">
								{t("search.no_results", { defaultValue: "لا توجد نتائج" })}
							</p>
						</div>
					) : (
						<ResultGroups
							result={query.data!}
							flatHits={flatHits}
							focusedIndex={focused}
							onSelect={go}
						/>
					)}
				</div>

				<div className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground flex items-center justify-between flex-wrap gap-2">
					<span>
						↑↓ {t("search.nav", { defaultValue: "تنقل" })} · ↵{" "}
						{t("search.open", { defaultValue: "افتح" })} · Esc{" "}
						{t("search.close", { defaultValue: "إغلاق" })}
					</span>
					<span>
						{t("search.shortcut", { defaultValue: "اختصار: Ctrl+K" })}
					</span>
				</div>
			</div>
		</div>
	);
}

function EmptyHint() {
	const { t } = useTranslation("common");
	return (
		<div className="py-8 px-4 text-center text-sm text-muted-foreground">
			<p>{t("search.hint_title")}</p>
			<ul className="mt-3 text-xs space-y-1.5 text-muted-foreground inline-flex flex-col items-start">
				<li className="flex items-center gap-2">
					<User className="size-3.5 shrink-0" strokeWidth={2} aria-hidden />{" "}
					{t("search.hint_name")}
				</li>
				<li className="flex items-center gap-2">
					<Phone className="size-3.5 shrink-0" strokeWidth={2} aria-hidden />{" "}
					{t("search.hint_phone")}
				</li>
				<li className="flex items-center gap-2">
					<IdCard className="size-3.5 shrink-0" strokeWidth={2} aria-hidden />{" "}
					{t("search.hint_mrn")}
				</li>
				<li className="flex items-center gap-2">
					<Receipt className="size-3.5 shrink-0" strokeWidth={2} aria-hidden />{" "}
					{t("search.hint_invoice")}
				</li>
			</ul>
		</div>
	);
}

function ResultGroups({
	result,
	flatHits,
	focusedIndex,
	onSelect,
}: {
	result: SearchResult;
	flatHits: SearchHit[];
	focusedIndex: number;
	onSelect: (h: SearchHit) => void;
}) {
	const { t } = useTranslation("common");
	function indexOf(h: SearchHit) {
		return flatHits.findIndex((x) => x === h);
	}

	return (
		<>
			{result.patients.length > 0 && (
				<Group icon={User} label={t("search.group_patients")}>
					{result.patients.map((h) => (
						<RowPatient
							key={h.id}
							hit={h}
							focused={indexOf(h) === focusedIndex}
							onClick={() => onSelect(h)}
						/>
					))}
				</Group>
			)}
			{result.appointments.length > 0 && (
				<Group icon={CalendarDays} label={t("search.group_appointments")}>
					{result.appointments.map((h) => (
						<RowAppointment
							key={h.id}
							hit={h}
							focused={indexOf(h) === focusedIndex}
							onClick={() => onSelect(h)}
						/>
					))}
				</Group>
			)}
			{result.invoices.length > 0 && (
				<Group icon={Receipt} label={t("search.group_invoices")}>
					{result.invoices.map((h) => (
						<RowInvoice
							key={h.id}
							hit={h}
							focused={indexOf(h) === focusedIndex}
							onClick={() => onSelect(h)}
						/>
					))}
				</Group>
			)}
		</>
	);
}

function Group({
	icon: Icon,
	label,
	children,
}: {
	icon: LucideIcon;
	label: string;
	children: React.ReactNode;
}) {
	return (
		<div className="py-1">
			<div className="px-3 py-1 text-xs uppercase text-muted-foreground font-semibold flex items-center gap-1.5">
				<Icon className="size-3.5" strokeWidth={2} aria-hidden />
				{label}
			</div>
			<ul>{children}</ul>
		</div>
	);
}

function rowClass(focused: boolean) {
	return `flex items-center justify-between gap-3 px-3 py-2 cursor-pointer ${
		focused ? "bg-brand-50 dark:bg-brand-950/40" : "hover:bg-muted"
	}`;
}

function RowPatient({
	hit,
	focused,
	onClick,
}: {
	hit: import("@klyra/shared").SearchHitPatient;
	focused: boolean;
	onClick: () => void;
}) {
	return (
		<li>
			<button
				type="button"
				onClick={onClick}
				className={`w-full text-start ${rowClass(focused)}`}
			>
				<div className="min-w-0">
					<p className="text-sm font-medium truncate">{hit.full_name}</p>
					<p className="text-xs text-muted-foreground truncate" dir="ltr">
						{hit.mrn}
						{hit.phone ? ` · ${hit.phone}` : ""}
					</p>
				</div>
				<span aria-hidden className="text-muted-foreground shrink-0">
					↵
				</span>
			</button>
		</li>
	);
}

function RowAppointment({
	hit,
	focused,
	onClick,
}: {
	hit: import("@klyra/shared").SearchHitAppointment;
	focused: boolean;
	onClick: () => void;
}) {
	const { t } = useTranslation("common");
	return (
		<li>
			<button
				type="button"
				onClick={onClick}
				className={`w-full text-start ${rowClass(focused)}`}
			>
				<div className="min-w-0">
					<p className="text-sm font-medium truncate">{hit.patient_name}</p>
					<p className="text-xs text-muted-foreground truncate">
						{hit.title ?? statusLabel(t, hit.status)}
						{hit.doctor_name ? ` · ${hit.doctor_name}` : ""}
					</p>
				</div>
				<DateText
					value={hit.scheduled_at}
					withTime
					className="text-xs text-muted-foreground shrink-0"
				/>
			</button>
		</li>
	);
}

function RowInvoice({
	hit,
	focused,
	onClick,
}: {
	hit: import("@klyra/shared").SearchHitInvoice;
	focused: boolean;
	onClick: () => void;
}) {
	const { t, i18n } = useTranslation("common");
	return (
		<li>
			<button
				type="button"
				onClick={onClick}
				className={`w-full text-start ${rowClass(focused)}`}
			>
				<div className="min-w-0">
					<p className="text-sm font-medium font-mono truncate">
						{hit.invoice_number}
					</p>
					<p className="text-xs text-muted-foreground truncate">
						{hit.patient_name} · {statusLabel(t, hit.status)}
					</p>
				</div>
				<span
					className="text-xs tabular-nums text-muted-foreground shrink-0 inline-flex items-center gap-1"
					dir="ltr"
				>
					{formatNumber(hit.total_amount, i18n.language)} {hit.currency} ·{" "}
					<DateText
						value={hit.created_at}
						opts={{ month: "short", day: "numeric" }}
					/>
				</span>
			</button>
		</li>
	);
}

function flatten(r: SearchResult | undefined): SearchHit[] {
	if (!r) return [];
	return [...r.patients, ...r.appointments, ...r.invoices];
}
